/**
 * useTagWalletWithName — R5-RI
 *
 * Mutation hook for POST /api/v2/wallets/:id/tag_with_wallet_name — v1-Paritaet
 * zum One-Click-Tagging aus user_wallets_controller.rb#tag_hotspots: der Tag wird
 * serverseitig aus dem aktuellen Wallet-Namen erzeugt/gefunden (find_or_create_by)
 * und allen Hotspots der Wallet zugewiesen. Keine Tag-Auswahl noetig — bewusst ein
 * ANDERER Pfad als der bestehende BulkTagModal-Flow (useBulkTagWalletHotspots),
 * der eine manuelle Mehrfach-Tag-Zuordnung ueber tag_ids abbildet.
 *
 * R5-RI: /api/v2/wallets/{id}/tag_with_wallet_name wurde additiv nach
 * api-types/openapi.yaml nachgezogen (kein `as any`-Workaround).
 *
 * i18n100-1 (Cluster-C-Muster): der Hook wirft stabile Fehler-CODES statt
 * Klartext — WalletDetail.tsx (Owner-UI) mappt den Code via t().
 */
import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

export type TagWithWalletNameErrorCode = 'WALLET_NAME_MISSING' | 'WALLET_NOT_FOUND' | 'TAG_FAILED'

export type TagWithWalletNameResult =
  | { ok: true; tagId: number; tagName: string; taggedCount: number; hotspotCount: number; message: string }
  | { ok: false; error: TagWithWalletNameErrorCode }

interface ApiErrorBody {
  status?: number
  body?: { code?: string } | string
}

async function tagWithWalletNameRequest(walletId: number): Promise<TagWithWalletNameResult> {
  try {
    const result = await apiClient.POST('/api/v2/wallets/{id}/tag_with_wallet_name', {
      params: { path: { id: walletId } },
    })

    if (result.error) {
      return { ok: false, error: 'TAG_FAILED' }
    }

    const json = result.data as {
      data?: { tag_id?: number; tag_name?: string; tagged_count?: number; hotspot_count?: number; message?: string }
    }
    const data = json?.data

    return {
      ok: true,
      tagId: data?.tag_id ?? 0,
      tagName: data?.tag_name ?? '',
      taggedCount: data?.tagged_count ?? 0,
      hotspotCount: data?.hotspot_count ?? 0,
      message: data?.message ?? '',
    }
  } catch (e) {
    const err = e as ApiErrorBody
    const status = err?.status
    const body = err?.body
    const code = typeof body === 'object' && body !== null ? body.code : undefined

    if (status === 422 && code === 'wallet_name_missing') {
      return { ok: false, error: 'WALLET_NAME_MISSING' }
    }
    if (status === 404) {
      return { ok: false, error: 'WALLET_NOT_FOUND' }
    }
    return { ok: false, error: 'TAG_FAILED' }
  }
}

export function useTagWalletWithName() {
  const queryClient = useQueryClient()
  const [isPending, setIsPending] = useState(false)

  const tagWithWalletName = useCallback(
    async (walletId: number): Promise<TagWithWalletNameResult> => {
      setIsPending(true)
      try {
        const outcome = await tagWithWalletNameRequest(walletId)
        // R5-Review F5: der Endpoint legt ggf. einen NEUEN Tag an und haengt ihn
        // an alle Hotspots der Wallet — ohne Invalidierung zeigen Tag-Liste und
        // Hotspot-Tabelle bis zum naechsten Reload den alten Stand.
        if (outcome.ok) {
          void queryClient.invalidateQueries({ queryKey: ['tags'] })
          void queryClient.invalidateQueries({ queryKey: ['hotspots'] })
        }
        return outcome
      } finally {
        setIsPending(false)
      }
    },
    [queryClient],
  )

  return { tagWithWalletName, isPending }
}
